import { FormDefinitionField, FormItem, FormTableDesign, FormTableRow, FormTableValue } from '@/interfaces/api/models';
import { decodeFormItemValue, getTableCellID, parseJsonSafely } from './form-helpers';

const REQUIRED_MESSAGE = 'This field is required';

function isEmpty(value: unknown, dataType: string): boolean {
    if (dataType === 'MultiSelect') return !Array.isArray(value) || value.length === 0;
    if (dataType === 'Number') return value === null || value === undefined;
    if (dataType === 'Checkbox') return false; // a checkbox is never "empty" — false is a valid answer
    return value === null || value === undefined || value === '';
}

/**
 * The rows a `Table` item currently *renders*, derived exactly the way `FormFieldTable` derives
 * them — a label-row table always shows one row per `RowDefinition` (whether or not the stored
 * value has an entry for it), a free-row table shows its stored rows or a single blank one.
 *
 * Deriving them the same way is what makes the synthesized error IDs line up with the cell IDs the
 * table computes, and it also means every error this validator raises is attached to a cell the
 * user can actually see and fill in. (The legacy validator iterated only the *stored* rows, so a
 * required column in a table the user never touched silently passed. That is the gap this fix
 * exists to close, so the row derivation deliberately follows the rendered table rather than the
 * legacy loop.)
 */
function renderedTableRows(design: FormTableDesign, parsed: FormTableValue): FormTableRow[] {
    const storedRows = parsed.Rows ?? [];
    if (design.LabelRows) {
        return (design.RowDefinitions ?? []).map((rowDef) => storedRows.find((r) => r.RowName === rowDef.Name) ?? { RowName: rowDef.Name, Columns: [] });
    }
    return storedRows.length > 0 ? storedRows : [{ RowName: null, Columns: [] }];
}

/**
 * Ports the legacy editor's table expansion (`manage-form.tsx`'s `validate`): a `Table` item is one
 * `FormItem`, but each required column of each row is independently required, so those cells are
 * expanded into their own validation entries keyed by `getTableCellID` — the same key
 * `FormFieldTable` gives the cell's renderer, so the error lands on the right control.
 */
function collectTableCellErrors(
    meta: FormItem,
    formDefinitionFields: FormDefinitionField[],
    values: Record<string, unknown>,
    errors: Record<string, string>,
): void {
    const field = formDefinitionFields.find((f) => f.ID === meta.FormDefinitionFieldID);
    if (!field) return;

    const design = parseJsonSafely<FormTableDesign>(field.DataOption, {} as FormTableDesign);
    const columns = design.ColumnDefinitions ?? [];
    if (columns.length === 0) return;

    // The live table JSON is in the form store; `meta.DataResult` is only the value it loaded with.
    const rawValue = values[meta.ID];
    const raw = typeof rawValue === 'string' ? rawValue : meta.DataResult;
    const rows = renderedTableRows(design, parseJsonSafely<FormTableValue>(raw, { Rows: [] }));

    for (const col of columns) {
        // Checkbox columns are skipped for the same reason top-level Checkboxes are: unticked is a
        // valid answer, not a missing one.
        if (!col.Required || col.DataType === 'Checkbox') continue;

        rows.forEach((row, rowIndex) => {
            const cellID = getTableCellID(col.Name, row.RowName ?? '', design.LabelRows ? '' : rowIndex, meta.SectionGroup ?? 0);
            const cell = (row.Columns ?? []).find((c) => c.ColumnName === col.Name);
            // Cell values are stored encoded; decode first so `isEmpty` sees the same shape it sees
            // for a top-level field of that DataType.
            if (isEmpty(decodeFormItemValue(cell?.Value ?? null, col.DataType), col.DataType)) {
                errors[cellID] = REQUIRED_MESSAGE;
            }
        });
    }
}

export function buildValidator(formItemsMeta: FormItem[], formDefinitionFields: FormDefinitionField[] = []) {
    return (values: Record<string, unknown>): Record<string, string> => {
        const errors: Record<string, string> = {};

        for (const meta of formItemsMeta) {
            // A table's required *columns* are validated per cell regardless of whether the table
            // item itself is marked Required; the top-level check below still applies unchanged.
            if (meta.DataType === 'Table') collectTableCellErrors(meta, formDefinitionFields, values, errors);

            if (!meta.Required) continue;
            if (meta.DataType === 'InformationalText' || meta.DataType === 'InformationalImage') continue;

            const value = values[meta.ID];
            if (isEmpty(value, meta.DataType ?? '')) {
                errors[meta.ID] = REQUIRED_MESSAGE;
            }
        }

        return errors;
    };
}
